/**
 * @file PreviewSection.tsx
 * @module PreviewSection
 * @description
 *   Renders a preview section for campaign assets, allowing users to
 *   toggle between selected, unselected, and video preview states.
 *   Handles loading and displaying assets from presigned URLs and provides
 *   fallback placeholders when assets are missing.
 */

import { useState, useEffect, useRef, memo, useCallback, useMemo } from 'react';
import { KatButton, KatAlert } from '@amzn/katal-react';
import { PreviewAssets, PreviewSectionState } from 'src/types';
import { generatePreview } from 'src/utils/preview/PreviewGenerator';
import { getPreviewConfig } from 'src/configs/previewConfig';
import { useSelection } from 'src/contexts/SelectionContext';
import styles from './PreviewSection.module.css';
import { ErrorConstants, PLACEHOLDER_URLS, PreviewConstants, StateConstants } from 'src/constants';
import { useAssetContext } from 'src/hooks/useAssetContext';
import { useCampaignMetadata } from 'src/hooks/useCampaignMetadata';
import { AssetType, DeviceFamily } from '@amzn/fire-tv-campaign-automation-service';
import { getControlPlaneClient } from 'src/contexts/ClientContext';
import { logger } from 'src/logger';
import { AssetContextState } from 'src/contexts/AssetContext';
import {
  getPreviewChanges,
  areAnyAssetsInLoadingStates,
  getAssetUrls,
  PreviewAssetStates,
  PLACEMENT_PLACEHOLDER_URLS,
} from 'src/utils/preview/previewUtils';
import { LoadingOverlay } from 'src/components/general/LoadingOverlay';
import { DownloadDropdown } from './DownloadDropdown';
import { PreviewControl } from './PreviewControl';
import {
  PreviewControlConfig,
  PreviewControlKey,
  PreviewControlValue,
  PreviewState,
  PreviewConfigBase,
  FireTVPreviewState,
  FireTVControlKey,
  TabletControlKey,
} from 'src/configs/previewConfigurations/types';
import { DEVICE_TYPE_DEFAULTS } from 'src/configs/previewConfigurations/FireTablets/constants';
import { TabletDeviceType } from 'src/configs/previewConfigurations/types';
import { DevicePreview } from './devicePreview/DevicePreview';
import { useDevicePreviewNavigation } from './devicePreview/DevicePreviewNavigationContext';

/**
 * Props for the PreviewContent component
 * @interface PreviewContentProps
 * @property {string} activeConfigKey - Currently active preview config key
 * @property {string} url - URL of the preview content
 * @property {boolean} hasVideo - Whether video content is available
 * @property {string} altText - Alt text for the image
 */
interface PreviewContentProps {
  activeState: PreviewState;
  url: string;
  hasVideo: boolean;
  altText: string;
}

/**
 * Renders preview content based on active state
 * @component PreviewContent
 * @param {PreviewContentProps} props - Component props
 * @returns {JSX.Element} Preview content UI
 */
const PreviewContent = memo(
  ({ activeState: activeState, url, hasVideo, altText }: PreviewContentProps) => {
    if (activeState === FireTVPreviewState.VIDEO) {
      return hasVideo ? (
        <video src={url} controls className={styles.preview} data-testid="preview-video" />
      ) : (
        <img
          src={url}
          alt={altText}
          className={styles.preview}
          data-testid={`preview-placeholder-image-${activeState.toLowerCase()}`}
        />
      );
    }
    if (activeState === FireTVPreviewState.DEVICE_PREVIEW) {
      return <DevicePreview />;
    }

    return (
      <img
        src={url}
        alt={altText}
        className={styles.preview}
        data-testid={`preview-image-${activeState.toLowerCase()}`}
      />
    );
  },
);

const initializeControlValues = (
  config: PreviewConfigBase,
): Record<PreviewControlKey, PreviewControlValue> => {
  const values = {} as Record<PreviewControlKey, PreviewControlValue>;
  config.controls.forEach((control: PreviewControlConfig) => {
    values[control.key] = control.defaultValue as PreviewControlValue;
  });
  return values;
};

/**
 * Main preview section component
 * Manages preview states and asset loading
 * @component PreviewSection
 * @returns {JSX.Element} Complete preview section UI
 */
const PreviewSection = memo(() => {
  const { placement, deviceFamily } = useSelection();

  const config = useMemo(
    () => getPreviewConfig(placement, deviceFamily),
    [placement, deviceFamily],
  );

  // --- State ---
  const [controlValues, setControlValues] = useState<
    Record<PreviewControlKey, PreviewControlValue>
  >(() => initializeControlValues(config));

  const [previewUrlCache, setPreviewUrlCache] = useState<Record<string, string>>({});

  const [previewSectionState, setPreviewSectionState] = useState<PreviewSectionState>({
    urls: {},
    isLoading: false,
    activeState: config.defaultState,
    error: undefined,
  });

  // Auto-switch to Device Preview tab when triggered (FireTV only)
  const { isTestCampaignModalOpen } = useDevicePreviewNavigation();
  useEffect(() => {
    if (isTestCampaignModalOpen && deviceFamily === DeviceFamily.FIRE_TV) {
      setControlValues((prev) => ({
        ...prev,
        [FireTVControlKey.STATE]: FireTVPreviewState.DEVICE_PREVIEW,
      }));
      setPreviewSectionState((prev) => ({
        ...prev,
        activeState: FireTVPreviewState.DEVICE_PREVIEW,
      }));
    }
  }, [isTestCampaignModalOpen, deviceFamily]);

  const updatePreviewSectionState = useCallback((updates: Partial<PreviewSectionState>) => {
    setPreviewSectionState((prev) => {
      const newState = { ...prev, ...updates };
      return newState;
    });
  }, []);

  const { assetStates } = useAssetContext();
  const { metadata } = useCampaignMetadata();

  const previewStateRef = useRef<PreviewAssetStates>({
    assets: {} as AssetContextState,
    bodyCopy: '',
  });

  const controlPlaneClient = getControlPlaneClient();

  const assetUrlsRef = useRef<PreviewAssets>(
    PLACEMENT_PLACEHOLDER_URLS[placement] as PreviewAssets,
  );

  /**
   * Generates preview URL for given state
   *
   * @param previewState - The state to generate preview for
   * @returns Promise resolving to Record<stateName, previewUrl>
   * @example { 'Unselected': 'data:image...' } or { '10Inch_Landscape_FOS': 'data:image...' }
   */
  const generatePreviews = useCallback(
    async (previewState: PreviewState): Promise<Record<string, string>> => {
      const previewAssets = {
        [AssetType.BACKGROUND_IMAGE]: assetUrlsRef.current[AssetType.BACKGROUND_IMAGE],
        [AssetType.LOGO]: assetUrlsRef.current[AssetType.LOGO],
        [AssetType.VIDEO_TRAILER]: assetUrlsRef.current[AssetType.VIDEO_TRAILER],
        bodyCopy: metadata.featureRotatorText || '',
      };

      // For video state, return video URL directly (or placeholder if not available)
      if (previewState === FireTVPreviewState.VIDEO) {
        const videoUrl = assetUrlsRef.current[AssetType.VIDEO_TRAILER];
        return { [previewState]: videoUrl || PLACEHOLDER_URLS[AssetType.VIDEO_TRAILER] };
      }

      const previewUrl = await generatePreview(previewAssets, previewState, config);

      return { [previewState]: previewUrl };
    },
    [placement, deviceFamily, metadata.featureRotatorText],
  );

  const isMounted = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  // --- Effects ---

  /** Setup AbortController on mount, cleanup on unmount */
  useEffect(() => {
    abortControllerRef.current = new AbortController();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, []);

  /** Unified effect: handles asset changes and control value changes with caching */
  useEffect(() => {
    isMounted.current = true;

    // Check if aborted before proceeding
    if (!abortControllerRef.current || abortControllerRef.current.signal.aborted) {
      return;
    }
    const signal = abortControllerRef.current.signal;

    /** Step 1: check if any asset is in loading state,
     *  If yes, put the preview section in loading state and return
     *  Else, continue */
    if (areAnyAssetsInLoadingStates(assetStates)) {
      updatePreviewSectionState({
        isLoading: true,
      });
      return;
    }
    logger.debug('Asset states : ', { assetStates });
    /** Step 2: Check what changed */
    const changes = getPreviewChanges(
      { assets: assetStates, bodyCopy: metadata.featureRotatorText || '' },
      previewStateRef.current,
    );
    const resolvedState = config.behavior.stateResolution(controlValues);

    // Device Preview renders a live component, skip canvas generation
    if (resolvedState === FireTVPreviewState.DEVICE_PREVIEW) {
      updatePreviewSectionState({ activeState: resolvedState, isLoading: false, error: undefined });
      return;
    }

    // Step 3: Assets or body copy changed - flush cache and regenerate
    if (changes.assets.length > 0 || changes.hasBodyCopyChanged) {
      /** If any assets are changed - new preview need to be generated with new asset urls
       *  So we can safely put preview section to loading.
       * and flush url cache
       */
      updatePreviewSectionState({ isLoading: true });
      setPreviewUrlCache({});

      /** Method to update the asset urls and generate previews */
      const updateAssetsAndGeneratePreviews = async () => {
        try {
          // Fetch asset URLs only if assets changed
          if (changes.assets.length > 0) {
            const changedAssets = changes.assets.map((type) => {
              const asset = assetStates[type];
              return { type, key: asset.key };
            });
            /** Step 4: Get the asset urls for the changed assets */
            const assetToAssetUrlsMap = await getAssetUrls(
              changedAssets,
              controlPlaneClient,
              placement,
            );

            // Check if aborted after async operation
            if (signal?.aborted) return;

            /** Step 5: Update the asset refs*/
            Object.entries(assetToAssetUrlsMap).forEach(([type, assetUrl]) => {
              assetUrlsRef.current[type as AssetType] = assetUrl;
            });
          }
          /** Step 6: Generate the previews using latest asset refs */
          const previewUrls = await generatePreviews(resolvedState);

          // Check if aborted before updating state
          if (signal?.aborted) return;

          /** Step 7: Update cache and update the preview urls for the respective states and set the loading to false */
          setPreviewUrlCache({ [resolvedState]: previewUrls[resolvedState] });
          updatePreviewSectionState({
            urls: previewUrls,
            activeState: resolvedState,
            isLoading: false,
          });
        } catch (err) {
          // Don't update state if aborted
          if (signal?.aborted) return;

          logger.error(ErrorConstants.MESSAGES.ERROR_UPDATING_PRESIGNED_URLS, { error: err });
          updatePreviewSectionState({
            error: ErrorConstants.MESSAGES.FAILED_TO_UPDATE_PREVIEW_ASSETS,
            isLoading: false,
          });
        }
      };

      updateAssetsAndGeneratePreviews();
    } else {
      // Step 4: No asset changes - check cache for current state
      if (previewUrlCache[resolvedState]) {
        // Cache hit - just update active state
        updatePreviewSectionState({ activeState: resolvedState });
      } else {
        // Cache miss - generate and cache
        const generateAndCache = async () => {
          updatePreviewSectionState({ isLoading: true });
          try {
            const previewUrls = await generatePreviews(resolvedState);

            // Check if aborted before updating state
            if (signal?.aborted) return;

            setPreviewUrlCache((prev) => ({
              ...prev,
              [resolvedState]: previewUrls[resolvedState],
            }));
            setPreviewSectionState((prev) => ({
              ...prev,
              activeState: resolvedState,
              urls: { ...prev.urls, ...previewUrls },
              isLoading: false,
            }));
          } catch (err) {
            // Don't update state if aborted
            if (signal?.aborted) return;

            logger.error('Failed to generate preview', { error: err });
            updatePreviewSectionState({
              error: ErrorConstants.MESSAGES.FAILED_TO_UPDATE_PREVIEW_ASSETS,
              isLoading: false,
            });
          }
        };
        generateAndCache();
      }
    }

    previewStateRef.current = { assets: assetStates, bodyCopy: metadata.featureRotatorText || '' };

    return () => {
      isMounted.current = false;
    };
  }, [
    assetStates,
    controlValues,
    metadata.featureRotatorText,
    controlPlaneClient,
    generatePreviews,
    updatePreviewSectionState,
  ]);

  // --- Handlers ---

  const hasVideoAsset = !!assetStates[AssetType.VIDEO_TRAILER]?.key;

  // Handle control value changes
  const handleControlChange = useCallback(
    (key: PreviewControlKey, value: PreviewControlValue) => {
      let newValues = { ...controlValues, [key]: value };

      // Auto-reset using config (FireTablets only)
      if (key === TabletControlKey.DEVICE_TYPE && deviceFamily === DeviceFamily.FIRE_TABLETS) {
        const defaults = DEVICE_TYPE_DEFAULTS[value as TabletDeviceType];
        if (defaults) {
          newValues[TabletControlKey.SIZE] = defaults.size as PreviewControlValue;
          newValues[TabletControlKey.ORIENTATION] = defaults.orientation as PreviewControlValue;
        }
      }

      setControlValues(newValues);

      // Resolve the state using device-specific logic
      const resolvedState = config.behavior.stateResolution(newValues);

      // Update active state
      updatePreviewSectionState({ activeState: resolvedState });
    },
    [controlValues, config, deviceFamily, updatePreviewSectionState],
  );

  // --- Render ---
  return (
    <section className={styles.container}>
      <div className={styles.content}>
        {/* Unified control rendering - config-driven for all devices */}
        {config.controls.length > 0 && (
          <div className={styles.controls}>
            {config.controls.map((control) => (
              <PreviewControl
                key={control.key}
                control={control}
                value={controlValues[control.key] || control.defaultValue}
                onChange={(value) => handleControlChange(control.key, value as PreviewControlValue)}
                controlValues={controlValues}
              />
            ))}
          </div>
        )}

        {previewSectionState.isLoading ? (
          <LoadingOverlay text={PreviewConstants.LOADING_PREVIEW_TEXT} fullScreen={false} />
        ) : (
          <>
            {previewSectionState.error && (
              <KatAlert
                variant="danger"
                description={previewSectionState.error}
                data-testid="preview-error-alert"
              />
            )}

            <div className={styles.previewWrapper}>
              <PreviewContent
                activeState={previewSectionState.activeState}
                url={previewSectionState.urls[previewSectionState.activeState] || ''}
                hasVideo={hasVideoAsset}
                altText={config.stateConfigs[previewSectionState.activeState]?.altText || ''}
              />
            </div>

            <div className={styles.downloadContainer}>
              <DownloadDropdown
                assetUrls={assetUrlsRef.current}
                previewUrls={previewSectionState.urls}
                config={config}
                bodyCopy={metadata.featureRotatorText || ''}
              />
            </div>
          </>
        )}
      </div>
    </section>
  );
});

PreviewSection.displayName = PreviewConstants.COMPONENT_NAMES.PREVIEW_SECTION;
export { PreviewSection };
